// src/component/MM2026DialogAdmin.jsx
import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

export default function MM2026DialogAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  // 設定ドキュメントを購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", "mm2026JoinDialog");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          // enabled が未定義なら true 扱い
          setEnabled(data.enabled !== false);
        } else {
          // ドキュメントがまだない場合は「ON」がデフォルト
          setEnabled(true);
        }
        setLoading(false);
      },
      (err) => {
        console.error("MM2026DialogAdmin onSnapshot error:", err);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "設定の取得に失敗しました。",
          type: "error",
        });
      }
    );

    return () => unsub();
  }, []);

  const showSnack = useCallback((message, type = "success") => {
    setSnackbar({ open: true, message, type });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleToggle = async (event) => {
    const value = event.target.checked;
    setEnabled(value); // 先にローカル反映

    try {
      const ref = doc(db, "systemConfig", "mm2026JoinDialog");
      await setDoc(ref, { enabled: value }, { merge: true });
      showSnack(value ? "販促ダイアログを「表示する」に変更しました。" : "販促ダイアログを「表示しない」に変更しました。");
    } catch (err) {
      console.error("MM2026DialogAdmin update error:", err);
      showSnack("更新に失敗しました。時間をおいて再度お試しください。", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.03em",
            color: textBase,
          }}
        >
          MM2026 販促ダイアログ管理
        </Typography>

        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          「マスターマインドメンバーズ2026」の販促ダイアログを、全ユーザーに対して表示／非表示を切り替えます。
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <CampaignRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>

          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              MM2026 販促ダイアログ
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              ログインしたメンバーに対して、1日1回だけ「2026参加のご案内」ダイアログを自動表示するかどうかを切り替えます。
              2026・4期の支払い済みメンバーには、常に表示されません。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              {enabled ? "現在：表示する（有効）" : "現在：表示しない（停止中）"}
            </Typography>
          }
        />

        <Typography variant="body2" sx={{ mt: 1.5, color: "rgba(0,0,0,0.65)" }}>
          ・「表示しない」にすると、全メンバーの画面で販促ダイアログは開きません。  
          ・「表示する」に戻すと、支払い前のメンバーには、再び 1日1回のペースでダイアログが表示されます。
        </Typography>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={handleSnackbarClose}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
